import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { Injectable, Input } from "@angular/core";
import { Router } from "@angular/router";
import { Plugins } from "@capacitor/core";
import jwt_decode from "jwt-decode";
import { BehaviorSubject, from, Observable, throwError } from "rxjs";
import { catchError, map, switchMap, tap } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { JwtUserData } from "../interfaces/login-user-data";

export const TOKEN_KEY = "jwt-token";
const { Storage } = Plugins;

@Injectable({
  providedIn: "root",
})
export class ApiService {
  @Input() menu: string;
  // Init with null to filter out the first value in a guard!
  public isAuthenticated = new BehaviorSubject<{
    authenticated: boolean;
    token?: string;
    userData?: any;
  }>(null);
  private tokenExpirationTimer: any;

  constructor(private http: HttpClient, private router: Router) {
    this.loadToken();
  }

  private async loadToken(): Promise<void> {
    const token = await Storage.get({ key: TOKEN_KEY });
    if (token?.value) {
      try {
        const userData = jwt_decode(token.value);
        const isTokenValid = this.checkTokenExpiration(userData);

        if (isTokenValid) {
          this.isAuthenticated.next({
            authenticated: true,
            token: token.value,
            userData,
          });
          this.autoLogout(userData);
        } else {
          await this.clearAuthData();
        }
      } catch (error) {
        console.error("Token inválido:", error);
        await this.clearAuthData();
      }
    } else {
      this.isAuthenticated.next({ authenticated: false });
    }
  }

  private checkTokenExpiration(userData: any): boolean {
    if (!userData?.exp) return false;
    const expirationTime = userData.exp * 1000; // Convert to milliseconds
    return Date.now() < expirationTime;
  }

  private autoLogout(userData: any): void {
    if (!userData?.exp) return;

    const expirationTime = userData.exp * 1000 - Date.now();
    this.tokenExpirationTimer = setTimeout(() => {
      this.logout();
    }, expirationTime);
  }

  private async clearAuthData(): Promise<void> {
    await Storage.remove({ key: TOKEN_KEY });
    await Storage.remove({ key: "device" });
    if (this.tokenExpirationTimer) {
      clearTimeout(this.tokenExpirationTimer);
    }
    this.isAuthenticated.next({ authenticated: false });
  }

  login(credentials: { email: string; password: string }): Observable<any> {
    return this.http.post(`${environment.login}`, credentials).pipe(
      catchError(this.handleError),
      map((data: any) => data.access_token),
      switchMap((token) => {
        return from(this.handleAuthentication(token));
      })
    );
  }

  private async handleAuthentication(token: string): Promise<void> {
    await Storage.set({ key: TOKEN_KEY, value: token });
    const userData = jwt_decode(token);
    this.isAuthenticated.next({
      authenticated: true,
      token,
      userData,
    });
    this.autoLogout(userData);
  }

  async logout(): Promise<void> {
    await this.clearAuthData();
    this.router.navigateByUrl("/login", { replaceUrl: true });
  }

  // No ApiService
async loadTokenFromStorage(): Promise<string | null> {
  const { value } = await Storage.get({ key: TOKEN_KEY });
  if (value) {
    try {
      const userData = jwt_decode(value);
      if (this.checkTokenExpiration(userData)) {
        this.isAuthenticated.next({
          authenticated: true,
          token: value,
          userData
        });
        this.autoLogout(userData);
        return value;
      }
    } catch (error) {
      console.error('Token inválido:', error);
    }
    await this.clearAuthData();
  }
  return null;
}

  getUser(): JwtUserData {
    const authData = this.isAuthenticated.getValue();
    if (!authData?.authenticated) return null;

    const { id, email, nome } = authData.userData;
    return { id, email, nome };
  }

  getToken() {
    const authData = this.isAuthenticated.getValue();
    return authData?.authenticated ? authData : null;
  }

  isAuth(): boolean {
    const authData = this.isAuthenticated.getValue();
    return authData?.authenticated || false;
  }

  private handleError(error: HttpErrorResponse) {
    return throwError(() => new Error("Falha na autenticação"));
  }
}
